import { useState, useEffect, useCallback } from 'react';

export interface ColumnConfig {
  key: string;
  title: string;
  dataIndex?: string | string[];
  width: number;
  minWidth?: number;
  maxWidth?: number;
  visible: boolean;
  fixed?: 'left' | 'right' | false;
  order: number;
  resizable: boolean;
  draggable: boolean;
}

interface UserTableSettings {
  viewType: string;
  columns: Record<string, {
    width?: number;
    order?: number;
    visible?: boolean;
  }>;
  lastUpdated: string;
}

const STORAGE_KEY_PREFIX = 'table_column_settings_';

export const useTableColumns = (
  viewType: string,
  defaultColumns: ColumnConfig[]
) => {
  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);

  // 从 localStorage 加载配置
  const loadSettings = useCallback(() => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${viewType}`;
      const saved = localStorage.getItem(storageKey);
      
      if (saved) {
        const settings: UserTableSettings = JSON.parse(saved);
        
        // 合并默认配置和用户配置
        const mergedColumns = defaultColumns.map(col => {
          const userConfig = settings.columns[col.key];
          return {
            ...col,
            width: userConfig?.width ?? col.width,
            order: userConfig?.order ?? col.order,
            visible: userConfig?.visible ?? col.visible,
          };
        });
        
        // 按 order 排序
        mergedColumns.sort((a, b) => a.order - b.order);
        
        setColumns(mergedColumns);
        return mergedColumns;
      }
    } catch (error) {
      console.error('Failed to load column settings:', error);
    }
    
    return defaultColumns;
  }, [viewType, defaultColumns]);

  // 保存配置到 localStorage
  const saveSettings = useCallback((cols: ColumnConfig[]) => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${viewType}`;
      const settings: UserTableSettings = {
        viewType,
        columns: cols.reduce((acc, col) => {
          acc[col.key] = {
            width: col.width,
            order: col.order,
            visible: col.visible,
          };
          return acc;
        }, {} as Record<string, any>),
        lastUpdated: new Date().toISOString(),
      };
      
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save column settings:', error);
    }
  }, [viewType]);

  // 更新列宽
  const updateColumnWidth = useCallback((key: string, width: number) => {
    setColumns(prev => {
      const newColumns = prev.map(col => {
        if (col.key === key) {
          const minWidth = col.minWidth || 60;
          const maxWidth = col.maxWidth || 800;
          const finalWidth = Math.max(minWidth, Math.min(maxWidth, width));
          return { ...col, width: finalWidth };
        }
        return col;
      });
      saveSettings(newColumns);
      return newColumns;
    });
  }, [saveSettings]);

  // 更新列顺序
  const updateColumnOrder = useCallback((newOrder: string[]) => {
    setColumns(prev => {
      const newColumns = newOrder.map((key, index) => {
        const col = prev.find(c => c.key === key);
        return col ? { ...col, order: index } : null;
      }).filter(Boolean) as ColumnConfig[];
      
      saveSettings(newColumns);
      return newColumns;
    });
  }, [saveSettings]);

  // 更新列可见性
  const updateColumnVisibility = useCallback((key: string, visible: boolean) => {
    setColumns(prev => {
      const newColumns = prev.map(col => 
        col.key === key ? { ...col, visible } : col
      );
      saveSettings(newColumns);
      return newColumns;
    });
  }, [saveSettings]);

  // 重置为默认配置
  const resetSettings = useCallback(() => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${viewType}`;
      localStorage.removeItem(storageKey);
      setColumns(defaultColumns);
    } catch (error) {
      console.error('Failed to reset column settings:', error);
    }
  }, [viewType, defaultColumns]);

  // 初始化时加载配置
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    columns,
    updateColumnWidth,
    updateColumnOrder,
    updateColumnVisibility,
    resetSettings,
  };
};
